import axiosInstance from '../utils/axiosConfig';
import { getAbsoluteMediaUrl } from '../utils/mediaUtils';
import keepServerAnswer from '../utils/keepServerAnswer';

/**
 * Profile Service for Account Information Screen
 * Uses internal HMS system for employee data
 */
class ProfileService {
    /**
     * Get current user's complete profile
     * @returns {Promise<Object>} Profile data with privacy rules applied
     */
    async getProfile() {
        try {
            const response = await axiosInstance.get('/api/profile/me/');
            
            if (response.data.success) {
                return response.data.data;
            } else {
                throw new Error(response.data.error || 'Failed to fetch profile');
            }
        } catch (error) {
            console.error('Error fetching profile:', error);
            throw keepServerAnswer(error,
                error.response?.data?.error || 
                error.message || 
                'Failed to fetch profile information'
            );
        }
    }

    /**
     * Update user's profile information
     * @param {Object} profileData - Profile fields to update
     * @returns {Promise<Object>} Updated profile data
     */
    async updateProfile(profileData) {
        console.log('🔄 PROFILE_SERVICE: updateProfile() called');
        console.log('🔄 PROFILE_SERVICE: Profile data to send:', profileData);
        console.log('🔄 PROFILE_SERVICE: Data keys:', Object.keys(profileData));
        console.log('🔄 PROFILE_SERVICE: Data size:', JSON.stringify(profileData).length, 'characters');
        
        try {
            console.log('🔄 PROFILE_SERVICE: Making PUT request to /api/profile/me/update/');
            const requestStartTime = performance.now();
            
            const response = await axiosInstance.put('/api/profile/me/update/', profileData);
            
            const requestEndTime = performance.now();
            console.log('🔄 PROFILE_SERVICE: Request completed in', (requestEndTime - requestStartTime).toFixed(2), 'ms');
            console.log('🔄 PROFILE_SERVICE: Response status:', response.status);
            console.log('🔄 PROFILE_SERVICE: Response headers:', response.headers);
            console.log('🔄 PROFILE_SERVICE: Raw response data:', response.data);
            
            if (response.data.success) {
                console.log('✅ PROFILE_SERVICE: Update successful');
                console.log('✅ PROFILE_SERVICE: Server message:', response.data.message);
                console.log('✅ PROFILE_SERVICE: Updated profile data keys:', Object.keys(response.data.data || {}));
                
                return {
                    success: true,
                    message: response.data.message,
                    data: response.data.data
                };
            } else {
                console.log('❌ PROFILE_SERVICE: Update failed (success: false)');
                console.log('❌ PROFILE_SERVICE: Server error:', response.data.error);
                console.log('❌ PROFILE_SERVICE: Server errors:', response.data.errors);
                
                return {
                    success: false,
                    error: response.data.error,
                    errors: response.data.errors
                };
            }
        } catch (error) {
            console.error('❌ PROFILE_SERVICE: Exception caught in updateProfile:', error);
            console.error('❌ PROFILE_SERVICE: Error type:', error.constructor.name);
            console.error('❌ PROFILE_SERVICE: Error message:', error.message);
            
            if (error.response) {
                console.error('❌ PROFILE_SERVICE: Response error details:', {
                    status: error.response.status,
                    statusText: error.response.statusText,
                    data: error.response.data,
                    headers: error.response.headers
                });
            } else if (error.request) {
                console.error('❌ PROFILE_SERVICE: Request error (no response):', error.request);
            } else {
                console.error('❌ PROFILE_SERVICE: General error:', error.message);
            }
            
            // Handle validation errors
            if (error.response?.status === 400 && error.response.data?.errors) {
                console.log('❌ PROFILE_SERVICE: Handling validation errors');
                console.log('❌ PROFILE_SERVICE: Validation errors:', error.response.data.errors);
                
                return {
                    success: false,
                    error: 'Validation failed',
                    errors: error.response.data.errors
                };
            }
            
            const errorMessage = error.response?.data?.error || error.message || 'Failed to update profile';
            console.error('❌ PROFILE_SERVICE: Throwing error:', errorMessage);
            
            throw keepServerAnswer(error, errorMessage);
        }
    }

    /**
     * Upload avatar photo
     * @param {File} file - Image file to upload
     * @returns {Promise<Object>} Upload result with avatar URL
     */
    async uploadAvatar(file) {
        try {
            const formData = new FormData();
            formData.append('avatar', file);
            
            const response = await axiosInstance.post('/api/profile/avatar/upload/', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                // Add upload progress tracking if needed
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round(
                        (progressEvent.loaded * 100) / progressEvent.total
                    );
                    console.log(`Upload progress: ${percentCompleted}%`);
                }
            });
            
            if (response.data.success) {
                return {
                    success: true,
                    message: response.data.message,
                    avatarUrl: response.data.avatar_url
                };
            } else {
                return {
                    success: false,
                    error: response.data.error
                };
            }
        } catch (error) {
            console.error('Error uploading avatar:', error);
            
            // Handle specific upload errors
            if (error.response?.status === 400) {
                return {
                    success: false,
                    error: error.response.data?.error || 'Invalid file'
                };
            }
            
            throw keepServerAnswer(error,
                error.response?.data?.error || 
                error.message || 
                'Failed to upload avatar'
            );
        }
    }

    /**
     * Get dropdown options for profile form with position verification info
     * @returns {Promise<Object>} Dropdown options (offices, departments, positions with verification flags)
     */
    async getDropdownOptions() {
        try {
            const response = await axiosInstance.get('/api/position/choices/');
            
            return {
                offices: response.data.offices || [],
                departments: response.data.departments || [],
                positions: response.data.positions || [], // Now includes requires_verification flag
                workingCharacteristics: response.data.working_characteristics || [],
                languages: response.data.languages || []
            };
        } catch (error) {
            console.error('Error fetching dropdown options:', error);
            throw keepServerAnswer(error,
                error.response?.data?.error || 
                error.message || 
                'Failed to fetch form options'
            );
        }
    }

    /**
     * Request position change (handles both direct updates and verification requests)
     * @param {string} position - Position key to request
     * @returns {Promise<Object>} Result with verification status
     */
    async requestPositionChange(position) {
        try {
            const response = await axiosInstance.post('/api/position/request/', {
                position: position
            });
            
            return response.data;
        } catch (error) {
            console.error('Error requesting position change:', error);
            throw keepServerAnswer(error,
                error.response?.data?.message || 
                error.response?.data?.error || 
                error.message || 
                'Failed to request position change'
            );
        }
    }

    /**
     * Get current user's position verification requests
     * @returns {Promise<Object>} User's position requests
     */
    async getMyPositionRequests() {
        try {
            const response = await axiosInstance.get('/api/position/my-requests/');
            return response.data;
        } catch (error) {
            console.error('Error fetching position requests:', error);
            throw keepServerAnswer(error,
                error.response?.data?.error || 
                error.message || 
                'Failed to fetch position requests'
            );
        }
    }

    /**
     * Validate file before upload
     * @param {File} file - File to validate
     * @returns {Object} Validation result
     */
    validateAvatarFile(file) {
        const maxSize = 5 * 1024 * 1024; // 5MB
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
        
        if (!file) {
            return { valid: false, error: 'No file selected' };
        }
        
        if (!allowedTypes.includes(file.type)) {
            return { 
                valid: false, 
                error: 'Invalid file type. Please upload JPG, PNG, or GIF images only.' 
            };
        }
        
        if (file.size > maxSize) {
            return { 
                valid: false, 
                error: 'File size too large. Please upload images smaller than 5MB.' 
            };
        }
        
        return { valid: true };
    }

    /**
     * Format profile data for display
     * @param {Object} profile - Raw profile data
     * @returns {Object} Formatted profile data
     */
    formatProfileData(profile) {
        console.log('🎨 FORMAT_DATA: formatProfileData() called with:', profile);
        console.log('🎨 FORMAT_DATA: Raw languages_spoken:', profile.languages_spoken);
        console.log('🎨 FORMAT_DATA: Raw working_characteristics:', profile.working_characteristics);
        console.log('🎨 FORMAT_DATA: Raw position:', profile.position);
        console.log('🎨 FORMAT_DATA: Raw office:', profile.office);
        console.log('🎨 FORMAT_DATA: Raw department:', profile.department);
        
        const formatted = {
            ...profile,
            displayName: profile.preferred_name || `${profile.first_name} ${profile.last_name}`.trim() || profile.email,
            fullName: `${profile.first_name || ''} ${profile.last_name || ''}`.trim(),
            hasAvatar: !!(profile.avatar_photo),
            avatarUrl: getAbsoluteMediaUrl(profile.avatar_photo),
            // Format dates
            dateOfBirth: profile.date_of_birth ? new Date(profile.date_of_birth) : null,
            dateJoinedHMS: profile.date_joined_hms ? new Date(profile.date_joined_hms) : null,
            // Ensure arrays are properly initialized - convert snake_case to camelCase
            languagesSpoken: profile.languages_spoken || [],
            workingCharacteristics: profile.working_characteristics || [],
            // Business context
            businessContexts: {
                solidFuels: profile.business_solid_fuels,
                liquidFuels: profile.business_liquid_fuels
            },
            // Supervisor (admin-only field)
            supervisor: profile.supervisor || null,
            // Explicitly ensure position, office, and department are preserved from backend
            position: profile.position || null,
            office: profile.office || null,
            additional_offices: Array.isArray(profile.additional_offices) ? profile.additional_offices : [],
            department: profile.department || null
        };
        
        console.log('🎨 FORMAT_DATA: Formatted languagesSpoken:', formatted.languagesSpoken);
        console.log('🎨 FORMAT_DATA: Formatted workingCharacteristics:', formatted.workingCharacteristics);
        console.log('🎨 FORMAT_DATA: Formatted position:', formatted.position);
        console.log('🎨 FORMAT_DATA: Formatted office:', formatted.office);
        console.log('🎨 FORMAT_DATA: Formatted department:', formatted.department);
        console.log('🎨 FORMAT_DATA: Formatted data keys:', Object.keys(formatted));
        
        return formatted;
    }

    /**
     * Prepare profile data for API update
     * @param {Object} formData - Form data to prepare
     * @returns {Object} API-ready data
     */
    prepareUpdateData(formData) {
        console.log('🔧 PREPARE_DATA: prepareUpdateData() called');
        console.log('🔧 PREPARE_DATA: Input formData:', formData);
        console.log('🔧 PREPARE_DATA: Input keys:', Object.keys(formData));
        
        const updateData = { ...formData };
        console.log('🔧 PREPARE_DATA: Initial updateData copy created');
        
        // Handle office and department as IDs
        if (updateData.office && typeof updateData.office === 'object') {
            console.log('🔧 PREPARE_DATA: Converting office object to ID:', updateData.office);
            updateData.office_id = updateData.office.id;
            delete updateData.office;
            console.log('🔧 PREPARE_DATA: Office converted to office_id:', updateData.office_id);
        } else {
            console.log('🔧 PREPARE_DATA: No office conversion needed:', updateData.office);
        }

        // Handle additional entities (Main Entity stays in office_id above)
        if (Array.isArray(updateData.additional_offices)) {
            const mainId = updateData.office_id;
            updateData.additional_office_ids = updateData.additional_offices
                .map(o => (o && typeof o === 'object' ? o.id : o))
                .filter(id => id && id !== mainId);
            console.log('🔧 PREPARE_DATA: additional_office_ids:', updateData.additional_office_ids);
        }
        delete updateData.additional_offices;
        
        if (updateData.department && typeof updateData.department === 'object') {
            console.log('🔧 PREPARE_DATA: Converting department object to ID:', updateData.department);
            updateData.department_id = updateData.department.id;
            delete updateData.department;
            console.log('🔧 PREPARE_DATA: Department converted to department_id:', updateData.department_id);
        } else {
            console.log('🔧 PREPARE_DATA: No department conversion needed:', updateData.department);
        }
        
        // Handle position - position is always an object with id, code, name properties
        if (updateData.position && typeof updateData.position === 'object') {
            console.log('🔧 PREPARE_DATA: Converting position object to position_id:', updateData.position);
            // Extract the ID for the backend
            updateData.position_id = updateData.position.id;
            delete updateData.position;
            console.log('🔧 PREPARE_DATA: Position converted to position_id:', updateData.position_id);
        } else if (updateData.position) {
            console.log('🔧 PREPARE_DATA: Position has unexpected type, removing:', typeof updateData.position);
            delete updateData.position;
        }
        
        // Format date of birth - use local date to avoid timezone issues
        if (updateData.dateOfBirth instanceof Date) {
            // Format as YYYY-MM-DD using local date (not UTC)
            const year = updateData.dateOfBirth.getFullYear();
            const month = String(updateData.dateOfBirth.getMonth() + 1).padStart(2, '0');
            const day = String(updateData.dateOfBirth.getDate()).padStart(2, '0');
            const localDate = `${year}-${month}-${day}`;
            
            console.log('🔧 PREPARE_DATA: Converting dateOfBirth to local date format:', {
                original: updateData.dateOfBirth,
                converted: localDate,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
            });
            updateData.date_of_birth = localDate;
            delete updateData.dateOfBirth;
        } else {
            console.log('🔧 PREPARE_DATA: No dateOfBirth conversion needed:', updateData.dateOfBirth);
        }
        
        // Format date joined HMS - use local date to avoid timezone issues
        if (updateData.dateJoinedHMS instanceof Date) {
            // Format as YYYY-MM-DD using local date (not UTC)
            const year = updateData.dateJoinedHMS.getFullYear();
            const month = String(updateData.dateJoinedHMS.getMonth() + 1).padStart(2, '0');
            const day = String(updateData.dateJoinedHMS.getDate()).padStart(2, '0');
            const localDate = `${year}-${month}-${day}`;
            
            console.log('🔧 PREPARE_DATA: Converting dateJoinedHMS to local date format:', {
                original: updateData.dateJoinedHMS,
                converted: localDate,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
            });
            updateData.date_joined_hms = localDate;
            delete updateData.dateJoinedHMS;
        } else {
            console.log('🔧 PREPARE_DATA: No dateJoinedHMS conversion needed:', updateData.dateJoinedHMS);
        }
        
        // Handle business contexts
        if (updateData.businessContexts) {
            console.log('🔧 PREPARE_DATA: Converting businessContexts:', updateData.businessContexts);
            updateData.business_solid_fuels = updateData.businessContexts.solidFuels;
            updateData.business_liquid_fuels = updateData.businessContexts.liquidFuels;
            delete updateData.businessContexts;
            console.log('🔧 PREPARE_DATA: Business contexts converted:', {
                business_solid_fuels: updateData.business_solid_fuels,
                business_liquid_fuels: updateData.business_liquid_fuels
            });
        } else {
            console.log('🔧 PREPARE_DATA: No businessContexts conversion needed');
        }
        
        // Convert camelCase frontend fields to snake_case backend fields
        if (updateData.languagesSpoken !== undefined) {
            console.log('🔧 PREPARE_DATA: Converting languagesSpoken to languages_spoken:', updateData.languagesSpoken);
            updateData.languages_spoken = updateData.languagesSpoken;
            delete updateData.languagesSpoken;
        }
        
        if (updateData.workingCharacteristics !== undefined) {
            console.log('🔧 PREPARE_DATA: Converting workingCharacteristics to working_characteristics:', updateData.workingCharacteristics);
            updateData.working_characteristics = updateData.workingCharacteristics;
            delete updateData.workingCharacteristics;
        }
        
        // Clean up display fields that shouldn't be sent to API
        const fieldsToDelete = [
            'displayName', 'fullName', 'hasAvatar', 'avatarUrl', 'avatar_photo',
            'created_at', 'updated_at', 'email', 'hms_employee_id', 
            'supervisors', 'heads_of_department'
        ];
        const deletedFields = [];
        
        fieldsToDelete.forEach(field => {
            if (updateData[field] !== undefined) {
                deletedFields.push(field);
                delete updateData[field];
            }
        });
        
        if (deletedFields.length > 0) {
            console.log('🔧 PREPARE_DATA: Deleted display fields:', deletedFields);
        }
        
        console.log('🔧 PREPARE_DATA: Final prepared data:', updateData);
        console.log('🔧 PREPARE_DATA: Final data keys:', Object.keys(updateData));
        console.log('🔧 PREPARE_DATA: Final data size:', JSON.stringify(updateData).length, 'characters');
        
        return updateData;
    }
}

// Export singleton instance
const profileService = new ProfileService();
export default profileService;
